// useMaintenanceVendorSubmissions.jsx
// Hook for submitting maintenance vendor access requests

import { getDatabase, ref, set, get } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

function generateVendorRequestId() {
  return 'VND' + Math.random().toString(36).substring(2, 9).toUpperCase();
}

const useMaintenanceVendorSubmissions = () => {
  const database = getDatabase();
  const storage = getStorage();

  // Submit Maintenance Vendor Request
  // Returns { success, requestId, savedRequest, landlordEmail } so the caller
  // can immediately trigger approval emails without a second DB round-trip.
  const submitMaintenanceVendorRequest = async (
    listingId,
    vendorData,
    idDocumentFile
  ) => {
    if (!listingId) {
      throw new Error("Listing ID is required to submit the maintenance vendor request.");
    }

    try {
      const requestId = generateVendorRequestId();

      // ── Upload ID document ──────────────────────────────────────────────────
      let idDocumentUrl = null;
      if (idDocumentFile) {
        try {
          const sanitizedFileName = idDocumentFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const idDocRef = storageRef(
            storage,
            `listings/${listingId}/maintenance-vendors/${requestId}/id-document/${sanitizedFileName}`
          );
          const metadata = { contentType: idDocumentFile.type || 'application/octet-stream' };
          await uploadBytes(idDocRef, idDocumentFile, metadata);
          idDocumentUrl = await getDownloadURL(idDocRef);
        } catch (uploadError) {
          console.error("Failed to upload ID document:", uploadError);
          throw new Error(`Failed to upload ID document: ${uploadError.message}. Please check Firebase Storage rules.`);
        }
      }

      // ── Fetch property details (address + owner email) ──────────────────────
      let landlordEmail = "";
      let address = "", city = "", state = "";
      try {
        const propertyRef = ref(database, `listings/${listingId}`);
        const propertySnap = await get(propertyRef);
        if (propertySnap.exists()) {
          const p = propertySnap.val();
          address = p.address || "";
          city = p.city || "";
          state = p.state || "";

          // Resolve landlord email from owner uid
          const ownerId = p.ownerId || p.ownerUid;
          if (ownerId) {
            const userRef = ref(database, `users/${ownerId}`);
            const userSnap = await get(userRef);
            if (userSnap.exists()) {
              landlordEmail = userSnap.val().email || "";
            }
          }
        }
      } catch (propError) {
        console.error("Error fetching property/owner info:", propError);
        // Non-fatal: proceed without landlord email
      }

      // ── Build and persist the request ──────────────────────────────────────
      const savedRequest = {
        ...vendorData,
        requestId,
        listingId,
        address,
        city,
        state,
        requestType: 'maintenance_vendor',
        idDocumentUrl,
        submittedAt: Date.now(),
        status: 'approved',
      };

      const vendorRequestRef = ref(
        database,
        `listings/${listingId}/maintenanceVendorRequests/${requestId}`
      );
      await set(vendorRequestRef, savedRequest);

      console.log("[VENDOR SUBMISSION] Saved request:", { requestId, listingId, landlordEmail });

      return { success: true, requestId, savedRequest, landlordEmail };
    } catch (error) {
      console.error("Error submitting maintenance vendor request:", error);
      throw error;
    }
  };

  return { submitMaintenanceVendorRequest };
};

export default useMaintenanceVendorSubmissions;